import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  AssignmentLateRounded,
  AssignmentRounded,
  CalendarTodayRounded,
  CheckCircleRounded,
  ChevronRightRounded,
  ExpandMoreRounded,
  EventAvailableRounded,
  HistoryRounded,
  MenuBookRounded,
  RateReviewRounded,
  ScheduleRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  useAllAssignments,
  useEnrolledSections,
  usePortalCalendar,
} from '../hooks/use-portal-data';
import { SectionScheduleLine, useTimeRange } from '../components/ScheduleText';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import {
  deriveSectionLifecycle,
  formatDateShortEs,
  type SectionLifecycle,
} from '../util/lifecycle';
import { isGradeNew } from '../util/seen-grades';
import { todayInZoneWeb } from '@dosfilos/shared';
import { useTenantTimeZone } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { useOpenSurveys } from '../hooks/use-surveys';
import { PollRounded } from '@mui/icons-material';
import { formatDate } from '../../../lib/i18n-format';
import { usePortalViewing } from '../../../stores/portal-view.store';
import { PortalHomeFrame, usePortalHome } from '../home/PortalHome';
import { useClassroomOn } from '../hooks/use-classroom-on';
import { addDaysYmd, homeBlocks } from '../util/classroom';
import { calendarDay } from '../util/calendar-dates';

const COURSE_COLORS = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#ec4899',
  '#0ea5e9',
  '#8b5cf6',
  '#ef4444',
  '#14b8a6',
];

function getCourseColor(sectionId: string): string {
  let hash = 0;
  for (let i = 0; i < sectionId.length; i++) hash = (hash * 31 + sectionId.charCodeAt(i)) | 0;
  return COURSE_COLORS[Math.abs(hash) % COURSE_COLORS.length]!;
}

type TFn = TFunction<'sis'>;

function relativeDue(
  t: TFn,
  iso?: string,
): { label: string; color: 'error' | 'warning' | 'default' } {
  if (!iso) return { label: t('portal.dashboard.noDate'), color: 'default' };
  const now = new Date();
  const due = new Date(iso);
  const diffMs = due.getTime() - now.getTime();
  const diffHrs = diffMs / (1000 * 60 * 60);
  const diffDays = diffHrs / 24;
  if (diffHrs < 0) {
    const overdueDays = Math.floor(-diffDays);
    return {
      label:
        overdueDays === 0
          ? t('portal.dashboard.overdue')
          : t('portal.dashboard.overdueByDays', { count: overdueDays }),
      color: 'error',
    };
  }
  if (diffHrs < 24)
    return {
      label: t('portal.dashboard.dueInHours', { count: Math.round(diffHrs) }),
      color: 'error',
    };
  if (diffDays < 7)
    return {
      label: t('portal.dashboard.dueInDays', { count: Math.ceil(diffDays) }),
      color: 'warning',
    };
  return {
    label: formatDate(due, { day: 'numeric', month: 'short' }),
    color: 'default',
  };
}

type StudentSubmissionLike =
  | { id?: string; status?: string; submittedAt?: string; finalPoints?: number }
  | null
  | undefined;

/**
 * Submission-aware due chip. If the student already submitted, the chip reflects
 * the submission (on-time vs late) instead of comparing dueAt against `now` — an
 * on-time submission of a past-due assignment must never read as "atrasada".
 */
function dueChipInfo(
  t: TFn,
  iso: string | undefined,
  submission: StudentSubmissionLike,
): { label: string; color: 'error' | 'warning' | 'success' | 'default' } {
  const status = submission?.status;
  const submittedAt = submission?.submittedAt;
  if ((status === 'submitted' || status === 'graded' || status === 'returned') && submittedAt) {
    const late = !!iso && new Date(submittedAt).getTime() > new Date(iso).getTime();
    return late
      ? { label: t('portal.dashboard.submittedLate'), color: 'warning' }
      : { label: t('portal.dashboard.submitted'), color: 'success' };
  }
  return relativeDue(t, iso);
}

function FocusTile({
  accent,
  icon,
  label,
  title,
  subtitle,
  chipLabel,
  chipBg,
  chipColor,
  highlight,
  onClick,
}: {
  accent: string;
  icon: JSX.Element;
  label: string;
  title: string;
  subtitle: string;
  chipLabel: string;
  chipBg: string;
  chipColor: string;
  highlight?: boolean;
  onClick: () => void;
}): JSX.Element {
  // En la vista del portal, el detalle de la tarea no se abre: sin cursor de enlace.
  const viewing = usePortalViewing();
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 2,
        cursor: viewing ? 'default' : 'pointer',
        bgcolor: highlight ? `${accent}14` : 'rgba(255,255,255,0.02)',
        border: '1px solid',
        borderColor: highlight ? accent : '#262c36',
        transition: 'border-color 0.15s, transform 0.15s',
        '&:hover': { borderColor: accent, transform: 'translateY(-2px)' },
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: accent,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: 10,
          }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={700} color="#e6edf3" noWrap>
          {title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="#8b949e" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {subtitle}
          </Typography>
          <Chip
            size="small"
            label={chipLabel}
            sx={{ height: 20, fontWeight: 700, bgcolor: chipBg, color: chipColor }}
          />
        </Stack>
      </Box>
    </Box>
  );
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  // «Ver portal»: sin abrir encuestas ni el detalle de una tarea (fuera de la
  // versión 1 de la vista).
  const viewing = usePortalViewing();
  const openAssignment = (sectionId: string, id: string) => {
    if (!viewing) navigate(`/portal-v2/courses/${sectionId}/assignments/${id}`);
  };
  const { personId } = useStudentIdentity();
  const sectionsQ = useEnrolledSections();
  const assignmentsQ = useAllAssignments();
  const openSurveysQ = useOpenSurveys();

  const today = new Date();
  // Lifecycle is a DATE-only judgement → compare against the tenant's calendar day.
  const todayYmd = todayInZoneWeb(useTenantTimeZone(), today);
  const sevenDaysOut = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  // SIN AULA, EL INICIO NO HABLA DE TAREAS (util/classroom): ni «Tu semana», ni
  // el trabajo de cada curso, ni «Pendientes»; «Próximos 7 días» queda con las
  // clases, que salen del HORARIO de cada sección (g4), desde hoy en el
  // calendario del seminario. Con el aula encendida, todo como siempre.
  const classroom = useClassroomOn();
  const blocks = homeBlocks(classroom.on);
  // Días de calendario del seminario, con aula y sin ella (M4·B1).
  const fromIso = todayYmd;
  const toIso = addDaysYmd(todayYmd, 6);
  const calendarQ = usePortalCalendar(fromIso, toIso);
  const range = useTimeRange();

  const allAssignments = assignmentsQ.data ?? [];
  const enrolledRaw = sectionsQ.data?.enrolledSections ?? [];
  const [sortMode, setSortMode] = useState<'date' | 'name'>('date');

  const enrolled = useMemo(() => {
    const copy = [...enrolledRaw];
    if (sortMode === 'name') {
      copy.sort((a, b) => a.courseName.localeCompare(b.courseName, 'es'));
    } else {
      // by start date ascending; nulls go last
      copy.sort((a, b) => {
        const sa = a.startDate ?? '￿';
        const sb = b.startDate ?? '￿';
        return sa.localeCompare(sb);
      });
    }
    return copy;
  }, [enrolledRaw, sortMode]);

  const pending = useMemo(
    () =>
      allAssignments
        .filter((a) => {
          if (!a.studentSubmission) return true;
          const s = a.studentSubmission.status;
          return s === 'draft' || s === 'submitted';
        })
        .filter((a) => a.publishedAt)
        .sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? '')),
    [allAssignments],
  );

  // Returned for revision (no grade yet) = the teacher asked for corrections.
  const needsCorrection = useMemo(
    () =>
      allAssignments
        .filter(
          (a) =>
            a.publishedAt &&
            a.studentSubmission?.status === 'returned' &&
            a.studentSubmission?.finalPoints === undefined,
        )
        .sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? '')),
    [allAssignments],
  );

  const needsCorrectionBySection = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of needsCorrection) m.set(a.sectionId, (m.get(a.sectionId) ?? 0) + 1);
    return m;
  }, [needsCorrection]);

  const recentlyGraded = useMemo(
    () =>
      allAssignments
        .filter(
          (a) =>
            a.studentSubmission &&
            (a.studentSubmission.status === 'graded' ||
              (a.studentSubmission.status === 'returned' &&
                a.studentSubmission.finalPoints !== undefined)),
        )
        .sort((a, b) =>
          (b.studentSubmission?.submittedAt ?? '').localeCompare(
            a.studentSubmission?.submittedAt ?? '',
          ),
        )
        .slice(0, 5),
    [allAssignments],
  );

  // Grades the student hasn't seen yet (or that were re-graded) → flag as "new".
  const isNew = (sub: StudentSubmissionLike): boolean =>
    !!sub?.id && isGradeNew(personId, sub.id, sub.finalPoints);

  const newGradeCount = useMemo(
    () => recentlyGraded.filter((a) => isNew(a.studentSubmission)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentlyGraded, personId],
  );

  const newGradeBySection = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of recentlyGraded) {
      if (isNew(a.studentSubmission)) m.set(a.sectionId, (m.get(a.sectionId) ?? 0) + 1);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentlyGraded, personId]);

  const comingUp = calendarQ.data.slice(0, 8);

  // Next class session (calendar event of type 'session'), if any in range.
  const nextClass = useMemo(
    () => calendarQ.data.find((e) => e.type === 'session'),
    [calendarQ.data],
  );

  // Overdue pending assignments — past their due date and still not submitted.
  const overdue = useMemo(() => {
    const nowMs = today.getTime();
    return pending.filter((a) => a.dueAt && new Date(a.dueAt).getTime() < nowMs);
  }, [pending, today]);

  // Pending assignments due within the next 7 days (strictly upcoming).
  const dueThisWeek = useMemo(() => {
    const horizon = sevenDaysOut.getTime();
    const nowMs = today.getTime();
    return pending.filter((a) => {
      if (!a.dueAt) return false;
      const t = new Date(a.dueAt).getTime();
      return t >= nowMs && t <= horizon;
    });
  }, [pending, sevenDaysOut, today]);

  // Map sectionId → next pending assignment for the course card
  const nextDueBySection = useMemo(() => {
    const map = new Map<
      string,
      { name: string; dueAt: string; submission: StudentSubmissionLike }
    >();
    for (const a of pending) {
      if (!a.dueAt) continue;
      const existing = map.get(a.sectionId);
      if (!existing || a.dueAt < existing.dueAt) {
        map.set(a.sectionId, { name: a.name, dueAt: a.dueAt, submission: a.studentSubmission });
      }
    }
    return map;
  }, [pending]);

  const pendingBySection = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of pending) {
      map.set(a.sectionId, (map.get(a.sectionId) ?? 0) + 1);
    }
    return map;
  }, [pending]);

  // Group enrolled sections by lifecycle. Archived are intentionally hidden
  // from the dashboard — they live in /portal-v2/historial.
  const groupedSections = useMemo(() => {
    const groups: Record<SectionLifecycle, typeof enrolled> = {
      upcoming: [],
      active: [],
      concluded: [],
      archived: [],
    };
    for (const s of enrolled) {
      const lc = deriveSectionLifecycle(s, todayYmd);
      groups[lc].push(s);
    }
    return groups;
  }, [enrolled, todayYmd]);

  // El inicio sin pantalla vacía (bienvenida, expediente, qué sigue, contacto):
  // lo de abajo —los cursos, o el aviso de que no hay— es lo de siempre.
  const home = usePortalHome({
    any: enrolled.length > 0,
    visible:
      groupedSections.active.length +
        groupedSections.upcoming.length +
        groupedSections.concluded.length >
      0,
  });

  if (sectionsQ.isLoading || !home.ready || !classroom.settled) return <PortalLoading />;

  const empty = (
    <PortalEmpty
      icon={<MenuBookRounded fontSize="inherit" />}
      title={t('portal.dashboard.emptyTitle')}
      body={t('portal.dashboard.emptyBody')}
    />
  );
  if (enrolled.length === 0) return <PortalHomeFrame home={home} courses={null} empty={empty} />;

  const openSurveys = openSurveysQ.data ?? [];

  const courses = (
    <Stack spacing={4}>
      {/* Pending anonymous surveys — high-visibility callout. */}
      {openSurveys.length > 0 && (
        <Card
          variant="outlined"
          sx={{ borderRadius: 3, borderColor: 'primary.main', bgcolor: 'action.hover' }}
        >
          <CardActionArea
            disabled={viewing}
            onClick={() =>
              navigate(
                openSurveys.length === 1
                  ? `/portal-v2/surveys/${openSurveys[0]!.id}`
                  : '/portal-v2/surveys',
              )
            }
            sx={{ p: 2 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <PollRounded color="primary" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={700}>
                  {openSurveys.length === 1
                    ? t('portal.surveys.calloutOne')
                    : t('portal.surveys.calloutOther', { count: openSurveys.length })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('portal.surveys.calloutHint')}
                </Typography>
              </Box>
              <ChevronRightRounded color="action" />
            </Stack>
          </CardActionArea>
        </Card>
      )}

      {/* Hero — greeting + focus tiles (todo lo previo a "Mis cursos"). */}
      {blocks.week && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a2232 0%, #141a24 60%, #12161d 100%)',
            border: '1px solid #21262d',
            borderRadius: 3,
            p: { xs: 2.5, sm: 3.5 },
          }}
        >
          <Typography variant="caption" color="#8b949e" sx={{ letterSpacing: 0.5 }}>
            {formatDate(today, { weekday: 'long', day: 'numeric', month: 'long' })}
          </Typography>
          <Typography variant="h4" fontWeight={800} color="#e6edf3" sx={{ mt: 0.25 }}>
            {t('portal.dashboard.yourWeek')}
          </Typography>
          <Typography variant="body1" color="#8b949e" sx={{ mt: 0.5 }}>
            {pending.length === 0
              ? t('portal.dashboard.allCaughtUp')
              : pending.length === 1
                ? t('portal.dashboard.pendingSummaryOne', { count: pending.length })
                : t('portal.dashboard.pendingSummaryOther', { count: pending.length })}
            {overdue.length > 0 && (
              <Box component="span" sx={{ color: '#f87171', fontWeight: 700 }}>
                {` · ${
                  overdue.length === 1
                    ? t('portal.dashboard.overdueSummaryOne', { count: overdue.length })
                    : t('portal.dashboard.overdueSummaryOther', { count: overdue.length })
                }`}
              </Box>
            )}
            {dueThisWeek.length > 0 &&
              ` · ${
                dueThisWeek.length === 1
                  ? t('portal.dashboard.dueThisWeekSummaryOne', { count: dueThisWeek.length })
                  : t('portal.dashboard.dueThisWeekSummaryOther', { count: dueThisWeek.length })
              }`}
            {newGradeCount > 0 && (
              <Box component="span" sx={{ color: '#10b981', fontWeight: 700 }}>
                {` · ${
                  newGradeCount === 1
                    ? t('portal.dashboard.newGradeSummaryOne', { count: newGradeCount })
                    : t('portal.dashboard.newGradeSummaryOther', { count: newGradeCount })
                }`}
              </Box>
            )}
          </Typography>

          {(overdue.length > 0 ||
            needsCorrection.length > 0 ||
            dueThisWeek.length > 0 ||
            recentlyGraded.length > 0 ||
            nextClass) && (
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(240px, 1fr))' },
              }}
            >
              {/* Atrasada — máxima urgencia */}
              {overdue.length > 0 &&
                (() => {
                  const a = overdue[0]!;
                  const due = dueChipInfo(t, a.dueAt, a.studentSubmission);
                  return (
                    <FocusTile
                      accent="#f87171"
                      icon={<ScheduleRounded sx={{ fontSize: 18, color: '#3b0a0a' }} />}
                      label={
                        overdue.length > 1
                          ? t('portal.dashboard.tileOverdueCount', { count: overdue.length })
                          : t('portal.dashboard.tileOverdue')
                      }
                      title={a.name}
                      subtitle={a.courseCode}
                      chipLabel={due.label}
                      chipBg="#ef4444"
                      chipColor="#fff"
                      highlight
                      onClick={() => openAssignment(a.sectionId, a.id)}
                    />
                  );
                })()}

              {/* Para corregir — acción */}
              {needsCorrection.length > 0 &&
                (() => {
                  const a = needsCorrection[0]!;
                  return (
                    <FocusTile
                      accent="#f59e0b"
                      icon={<RateReviewRounded sx={{ fontSize: 18, color: '#1c1917' }} />}
                      label={t('portal.dashboard.tileToCorrect')}
                      title={a.name}
                      subtitle={a.courseCode}
                      chipLabel={
                        needsCorrection.length > 1
                          ? `+${needsCorrection.length - 1}`
                          : t('portal.dashboard.returned')
                      }
                      chipBg="#f59e0b"
                      chipColor="#1c1917"
                      onClick={() => openAssignment(a.sectionId, a.id)}
                    />
                  );
                })()}

              {/* Esta semana — urgencia */}
              {dueThisWeek.length > 0 &&
                (() => {
                  const a = dueThisWeek[0]!;
                  const due = dueChipInfo(t, a.dueAt, a.studentSubmission);
                  return (
                    <FocusTile
                      accent="#ef4444"
                      icon={<ScheduleRounded sx={{ fontSize: 18, color: '#fff' }} />}
                      label={
                        dueThisWeek.length > 1
                          ? t('portal.dashboard.tileNextOfWeek', { count: dueThisWeek.length })
                          : t('portal.dashboard.tileThisWeek')
                      }
                      title={a.name}
                      subtitle={a.courseCode}
                      chipLabel={due.label}
                      chipColor="#fff"
                      chipBg="#ef4444"
                      onClick={() => openAssignment(a.sectionId, a.id)}
                    />
                  );
                })()}

              {/* Calificación reciente — resultado */}
              {recentlyGraded.length > 0 &&
                (() => {
                  const a = recentlyGraded[0]!;
                  const fresh = isNew(a.studentSubmission);
                  const grade = a.studentSubmission?.finalPoints;
                  return (
                    <FocusTile
                      accent="#10b981"
                      icon={<RateReviewRounded sx={{ fontSize: 18, color: '#062b1f' }} />}
                      label={
                        fresh
                          ? t('portal.dashboard.tileNewGrade')
                          : t('portal.dashboard.tileLastGrade')
                      }
                      title={a.name}
                      subtitle={a.courseCode}
                      chipLabel={`${grade?.toFixed(1) ?? '—'} / ${a.pointsPossible}`}
                      chipBg="rgba(16,185,129,0.18)"
                      chipColor="#10b981"
                      highlight={fresh}
                      onClick={() => openAssignment(a.sectionId, a.id)}
                    />
                  );
                })()}

              {/* Próxima clase — referencia */}
              {nextClass &&
                (() => {
                  const d = calendarDay(nextClass.date);
                  const dayLabel = formatDate(d, {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                  });
                  return (
                    <FocusTile
                      accent="#0ea5e9"
                      icon={<EventAvailableRounded sx={{ fontSize: 18, color: '#06283a' }} />}
                      label={t('portal.dashboard.tileNextClass')}
                      title={nextClass.title}
                      subtitle={nextClass.courseCode}
                      chipLabel={`${dayLabel}${nextClass.startTime ? ` · ${range(nextClass.startTime, nextClass.endTime)}` : ''}`}
                      chipBg="rgba(14,165,233,0.18)"
                      chipColor="#7dd3fc"
                      onClick={() => navigate('/portal-v2/calendar')}
                    />
                  );
                })()}
            </Box>
          )}
        </Box>
      )}

      {/* Course cards — grouped by lifecycle */}
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          mb={0.5}
        >
          <Typography variant="overline" color="#8b949e" fontWeight={700}>
            {t('portal.dashboard.myCourses')}
          </Typography>
          {enrolled.length > 1 && (
            <ToggleButtonGroup
              size="small"
              value={sortMode}
              exclusive
              onChange={(_, v) => v && setSortMode(v)}
              sx={{
                '& .MuiToggleButton-root': {
                  py: 0.25,
                  px: 1,
                  fontSize: 11,
                  color: '#8b949e',
                  borderColor: '#21262d',
                  textTransform: 'none',
                  '&.Mui-selected': { color: '#e6edf3', bgcolor: 'rgba(99,102,241,0.18)' },
                },
              }}
            >
              <ToggleButton value="date">{t('portal.dashboard.sortByStartDate')}</ToggleButton>
              <ToggleButton value="name">{t('portal.dashboard.sortByName')}</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
        {groupedSections.active.length > 0 && (
          <SectionCardGrid
            sections={groupedSections.active}
            variant="active"
            nextDueBySection={nextDueBySection}
            pendingBySection={pendingBySection}
            needsCorrectionBySection={needsCorrectionBySection}
            newGradeBySection={newGradeBySection}
            showWork={blocks.courseCardWork}
            onOpen={(id) => navigate(`/portal-v2/courses/${id}`)}
          />
        )}
        {groupedSections.upcoming.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
              <EventAvailableRounded sx={{ color: '#6366f1', fontSize: 18 }} />
              <Typography variant="overline" color="#8b949e" fontWeight={700}>
                {t('portal.dashboard.upcoming')}
              </Typography>
              <Chip
                size="small"
                label={groupedSections.upcoming.length}
                sx={{
                  height: 18,
                  fontSize: 11,
                  bgcolor: 'rgba(99,102,241,0.18)',
                  color: '#a5b4fc',
                }}
              />
            </Stack>
            <SectionCardGrid
              sections={groupedSections.upcoming}
              variant="upcoming"
              nextDueBySection={nextDueBySection}
              pendingBySection={pendingBySection}
              needsCorrectionBySection={needsCorrectionBySection}
              newGradeBySection={newGradeBySection}
              showWork={blocks.courseCardWork}
              onOpen={(id) => navigate(`/portal-v2/courses/${id}`)}
            />
          </Box>
        )}
        {groupedSections.concluded.length > 0 && (
          <Accordion
            defaultExpanded={false}
            disableGutters
            sx={{
              mt: 3,
              bgcolor: 'transparent',
              boxShadow: 'none',
              border: '1px solid #21262d',
              borderRadius: 2,
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': { px: 2, minHeight: 48 },
              '& .MuiAccordionDetails-root': { px: 2, pt: 0, pb: 2 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRounded sx={{ color: '#8b949e' }} />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <HistoryRounded sx={{ color: '#8b949e', fontSize: 18 }} />
                <Typography variant="overline" color="#8b949e" fontWeight={700}>
                  {t('portal.dashboard.pastCourses')}
                </Typography>
                <Chip
                  size="small"
                  label={groupedSections.concluded.length}
                  sx={{ height: 18, fontSize: 11, bgcolor: '#21262d', color: '#8b949e' }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <SectionCardGrid
                sections={groupedSections.concluded}
                variant="past"
                nextDueBySection={nextDueBySection}
                pendingBySection={pendingBySection}
                needsCorrectionBySection={needsCorrectionBySection}
                newGradeBySection={newGradeBySection}
                showWork={blocks.courseCardWork}
                onOpen={(id) => navigate(`/portal-v2/courses/${id}`)}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 2, md: 3 },
          gridTemplateColumns: { xs: '1fr', md: blocks.pending ? 'repeat(2, 1fr)' : '1fr' },
        }}
      >
        {/* To Do — sólo tareas: sin aula no se muestra. */}
        {blocks.pending && (
          <Box sx={{ minWidth: 0 }}>
            <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2 }}>
              <Box sx={{ p: 2.5, borderBottom: '1px solid #21262d' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AssignmentLateRounded sx={{ color: '#f59e0b' }} fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
                    {t('portal.dashboard.pendingTitle')}
                  </Typography>
                  {pending.length > 0 && (
                    <Chip
                      size="small"
                      label={pending.length}
                      color="warning"
                      sx={{ ml: 'auto', height: 20, fontSize: 11 }}
                    />
                  )}
                </Stack>
              </Box>
              <Box sx={{ minHeight: 200 }}>
                {assignmentsQ.isLoading && (
                  <Box textAlign="center" py={4}>
                    <CircularProgress size={20} />
                  </Box>
                )}
                {!assignmentsQ.isLoading && pending.length === 0 && (
                  <Box textAlign="center" py={5}>
                    <CheckCircleRounded sx={{ color: '#10b981', fontSize: 32 }} />
                    <Typography variant="body2" color="#8b949e" sx={{ mt: 1 }}>
                      {t('portal.dashboard.noPending')}
                    </Typography>
                  </Box>
                )}
                {pending.slice(0, 8).map((a) => {
                  const dueInfo = dueChipInfo(t, a.dueAt, a.studentSubmission);
                  const color = getCourseColor(a.sectionId);
                  return (
                    <Box
                      key={a.id}
                      onClick={() => openAssignment(a.sectionId, a.id)}
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        borderBottom: '1px solid #21262d',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: viewing ? 'default' : 'pointer',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                      }}
                    >
                      <Box sx={{ width: 3, height: 28, bgcolor: color, borderRadius: 2 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} color="#e6edf3" noWrap>
                          {a.name}
                        </Typography>
                        <Typography variant="caption" color="#8b949e" noWrap>
                          {a.courseCode} ·{' '}
                          {t('portal.dashboard.points', { count: a.pointsPossible })}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={dueInfo.label}
                        color={dueInfo.color}
                        variant="outlined"
                      />
                      <ChevronRightRounded sx={{ color: '#6e7681' }} fontSize="small" />
                    </Box>
                  );
                })}
              </Box>
            </Card>
          </Box>
        )}

        {/* Coming Up — clases siempre; tareas sólo con aula. */}
        <Box sx={{ minWidth: 0 }}>
          <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2 }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid #21262d' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarTodayRounded sx={{ color: '#6366f1' }} fontSize="small" />
                <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
                  {t('portal.dashboard.next7Days')}
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ minHeight: 200 }}>
              {calendarQ.isLoading && (
                <Box textAlign="center" py={4}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!calendarQ.isLoading && comingUp.length === 0 && (
                <Box textAlign="center" py={5}>
                  <Typography variant="body2" color="#8b949e">
                    {t('portal.dashboard.nothingScheduled')}
                  </Typography>
                </Box>
              )}
              {comingUp.map((evt) => {
                const color = getCourseColor(evt.sectionId);
                // Una clase del horario es una fecha del seminario: mediodía,
                // para que ninguna zona la corra al día anterior.
                const date = calendarDay(evt.date);
                const dayLabel = formatDate(date, {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                });
                return (
                  <Box
                    key={`${evt.type}-${evt.id}`}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderBottom: '1px solid #21262d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ width: 3, height: 28, bgcolor: color, borderRadius: 2 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} color="#e6edf3" noWrap>
                        {evt.title}
                      </Typography>
                      <Typography variant="caption" color="#8b949e">
                        {dayLabel}
                        {evt.startTime && ` · ${range(evt.startTime, evt.endTime)}`}
                        {evt.location && ` · ${evt.location}`}
                        {' · '}
                        {evt.courseCode}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Card>
        </Box>
      </Box>

      {sectionsQ.error && <Alert severity="error">{t('portal.dashboard.loadError')}</Alert>}
    </Stack>
  );

  return <PortalHomeFrame home={home} courses={courses} empty={empty} />;
}

interface SectionCardGridProps {
  sections: Array<{
    id: string;
    courseCode: string;
    courseName: string;
    facultyName: string;
    credits: number;
    startDate?: string | null;
    endDate?: string | null;
    schedule?: Array<{ dayOfWeek: number; startTime: string; endTime: string; location?: string }>;
  }>;
  variant: 'active' | 'upcoming' | 'past';
  nextDueBySection: Map<string, { name: string; dueAt: string; submission: StudentSubmissionLike }>;
  pendingBySection: Map<string, number>;
  needsCorrectionBySection: Map<string, number>;
  newGradeBySection: Map<string, number>;
  /** El trabajo del aula en la tarjeta (entregas, «para corregir», nota nueva). */
  showWork: boolean;
  onOpen: (sectionId: string) => void;
}

function SectionCardGrid({
  sections,
  variant,
  nextDueBySection,
  pendingBySection,
  needsCorrectionBySection,
  newGradeBySection,
  showWork,
  onOpen,
}: SectionCardGridProps): JSX.Element {
  const { t } = useTranslation('sis');
  const isPast = variant === 'past';
  const isUpcoming = variant === 'upcoming';
  return (
    <Box
      sx={{
        mt: 0.5,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
      }}
    >
      {sections.map((s) => {
        const color = getCourseColor(s.id);
        // Sin aula, la tarjeta no habla de entregas ni de notas de tareas.
        const pendingForCourse = showWork ? (pendingBySection.get(s.id) ?? 0) : 0;
        const needsForCourse = showWork ? (needsCorrectionBySection.get(s.id) ?? 0) : 0;
        const newGradesForCourse = showWork ? (newGradeBySection.get(s.id) ?? 0) : 0;
        const nextDue = showWork ? nextDueBySection.get(s.id) : undefined;
        const nextDueInfo = nextDue ? dueChipInfo(t, nextDue.dueAt, nextDue.submission) : null;
        return (
          <Card
            key={s.id}
            sx={{
              bgcolor: '#161b22',
              border: '1px solid #21262d',
              borderRadius: 2,
              overflow: 'hidden',
              opacity: isPast ? 0.65 : 1,
              transition: 'border-color 0.15s, transform 0.15s, opacity 0.15s',
              '&:hover': {
                borderColor: color,
                transform: 'translateY(-2px)',
                opacity: isPast ? 0.9 : 1,
              },
            }}
          >
            <CardActionArea onClick={() => onOpen(s.id)} sx={{ p: 0, display: 'block' }}>
              <Box
                sx={{
                  height: 72,
                  bgcolor: color,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-end',
                  px: 2,
                  py: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,255,255,0.95)',
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                  }}
                >
                  {s.courseCode}
                </Typography>
                {needsForCourse > 0 && !isPast && (
                  <Chip
                    size="small"
                    label={t('portal.dashboard.cardToCorrect')}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      height: 22,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: '#f59e0b',
                      color: '#1c1917',
                    }}
                  />
                )}
                {newGradesForCourse > 0 && !isPast && (
                  <Chip
                    size="small"
                    label={t('portal.dashboard.cardNewGrade')}
                    sx={{
                      position: 'absolute',
                      top: needsForCourse > 0 ? 34 : 8,
                      left: 8,
                      height: 22,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: '#10b981',
                      color: '#062b1f',
                    }}
                  />
                )}
                {pendingForCourse > 0 && !isPast && (
                  <Chip
                    size="small"
                    label={pendingForCourse}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      height: 22,
                      minWidth: 22,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: 'rgba(0,0,0,0.45)',
                      color: '#fff',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                )}
                {isPast && (
                  <Chip
                    size="small"
                    label={t('portal.dashboard.cardFinished')}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      height: 22,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                    }}
                  />
                )}
                {isUpcoming && (
                  <Chip
                    size="small"
                    label={t('portal.dashboard.cardComingSoon')}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      height: 22,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="#e6edf3"
                  noWrap
                  sx={{ mb: 0.25 }}
                >
                  {s.courseName}
                </Typography>
                <Typography variant="caption" color="#8b949e" display="block" noWrap>
                  {s.facultyName} · {t('portal.dashboard.credits', { count: s.credits })}
                </Typography>
                {/* Sin aula, una tarjeta en curso muestra el horario (g4). */}
                {
                  <Box
                    sx={{
                      mt: 1.25,
                      pt: 1.25,
                      borderTop: '1px solid #21262d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      minHeight: 28,
                    }}
                  >
                    {!showWork && !isUpcoming && !isPast ? (
                      <SectionScheduleLine schedule={s.schedule} />
                    ) : isUpcoming ? (
                      <>
                        <EventAvailableRounded sx={{ fontSize: 14, color: '#a5b4fc' }} />
                        <Typography variant="caption" color="#c9d1d9">
                          {t('portal.dashboard.startsOn', {
                            date: formatDateShortEs(s.startDate),
                          })}
                        </Typography>
                      </>
                    ) : isPast ? (
                      <>
                        <HistoryRounded sx={{ fontSize: 14, color: '#8b949e' }} />
                        <Typography variant="caption" color="#8b949e">
                          {t('portal.dashboard.endedOn', {
                            date: formatDateShortEs(s.endDate),
                          })}
                        </Typography>
                      </>
                    ) : nextDue && nextDueInfo ? (
                      <>
                        <ScheduleRounded
                          sx={{
                            fontSize: 14,
                            color:
                              nextDueInfo.color === 'error'
                                ? '#f87171'
                                : nextDueInfo.color === 'warning'
                                  ? '#fbbf24'
                                  : '#8b949e',
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ flex: 1, minWidth: 0, color: '#c9d1d9' }}
                          noWrap
                        >
                          {nextDue.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color:
                              nextDueInfo.color === 'error'
                                ? '#f87171'
                                : nextDueInfo.color === 'warning'
                                  ? '#fbbf24'
                                  : '#8b949e',
                            flexShrink: 0,
                          }}
                        >
                          {nextDueInfo.label}
                        </Typography>
                      </>
                    ) : pendingForCourse > 0 ? (
                      // Hay trabajo pendiente, pero ninguno con fecha: `nextDue`
                      // sólo mira las tareas fechadas. Sin esta rama la tarjeta
                      // decía «Sin entregas pendientes» con el contador en 10 al
                      // lado, en la misma tarjeta.
                      <>
                        <AssignmentRounded sx={{ fontSize: 14, color: '#8b949e' }} />
                        <Typography variant="caption" color="#8b949e">
                          {t('portal.dashboard.pendingNoDueDate', { count: pendingForCourse })}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircleRounded sx={{ fontSize: 14, color: '#10b981' }} />
                        <Typography variant="caption" color="#8b949e">
                          {t('portal.dashboard.noSubmissionsPending')}
                        </Typography>
                      </>
                    )}
                  </Box>
                }
              </Box>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
}
